import * as Sentry from "@sentry/react";
import AWS from "aws-sdk";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { withAWSRetry, wrapLambdaClientWithCorrelation } from "~/auth/awsUtils";
import { type ServiceResult } from "~/error-handling";
import { getAutoCorrelationContext } from "~/error-handling/correlationContext";
import * as emailService from "~/services/email";
import * as lambdaService from "~/services/lambda";
import * as s3Service from "~/services/s3";

// Type helper to extract service function types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ServiceFunction = (...args: any[]) => Promise<any>;

// Type to wrap a service function with our error handling
type WrappedServiceFunction<F extends ServiceFunction> = (
  ...args: Parameters<F> extends [AWS.Lambda | AWS.S3, ...infer R]
    ? R
    : Parameters<F>
) => Promise<ServiceResult<Awaited<ReturnType<F>>>>;

// Type for wrapped service modules
export type WrappedServiceModule<T> = {
  [K in keyof T]: T[K] extends ServiceFunction
    ? WrappedServiceFunction<T[K]>
    : never;
};

// Type that combines lambda and email services
export type CombinedLambdaServices = typeof lambdaService &
  Pick<typeof emailService, keyof typeof emailService>;

export type Services = {
  /** Lambda service with retry, correlation context, Sentry instrumentation, and error notifications. */
  lambda: WrappedServiceModule<CombinedLambdaServices> | null;
  /** Same as `lambda` but suppresses user-facing error modals. Use for best-effort calls that are expected to fail. */
  silentLambda: WrappedServiceModule<CombinedLambdaServices> | null;
  /** S3 service with retry and error notifications. */
  s3: WrappedServiceModule<typeof s3Service> | null;
};

// Lambda-specific service wrapper that includes correlation context injection
function createLambdaServiceWrapper(lambdaService: AWS.Lambda, silent = false) {
  return function wrapLambdaFunction<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    F extends (...args: any[]) => Promise<any>,
  >(serviceFunction: F, functionKey: string) {
    const wrappedFunction = (
      ...args: Parameters<F> extends [AWS.Lambda, ...infer R] ? R : never
    ) => {
      // Generate correlation context once for this Lambda call
      const correlationContext = getAutoCorrelationContext();

      // Wrap the Lambda client to inject the same correlation context
      const correlationWrappedLambda = wrapLambdaClientWithCorrelation(
        lambdaService,
        correlationContext,
      );

      // Wrap the service call with Sentry span for latency instrumentation
      return Sentry.startSpan(
        {
          name: `lambda/${functionKey}`,
          op: "lambda.invoke",
          attributes: {
            "lambda.function_key": functionKey,
            ...(correlationContext?.correlationID && {
              "correlation.id": correlationContext.correlationID,
            }),
            ...(correlationContext?.appointmentID && {
              "correlation.appointment_id": correlationContext.appointmentID,
            }),
          },
        },
        async (span) => {
          const result = await withAWSRetry(
            () => serviceFunction(correlationWrappedLambda, ...args),
            1, // maxRetries
            { correlationContext, silent },
          );
          if (result.error) {
            span.setStatus({ code: 2, message: "error" });
          }
          return result;
        },
      );
    };

    return wrappedFunction;
  };
}

// S3-specific service wrapper that uses the generic error handling with retry logic
function createS3ServiceWrapper(s3Service: AWS.S3) {
  return function wrapS3Function<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    F extends (...args: any[]) => Promise<any>,
  >(serviceFunction: F) {
    const wrappedFunction = (
      ...args: Parameters<F> extends [AWS.S3, ...infer R] ? R : never
    ) => {
      // Wrap the service call with automatic retry and error reporting logic
      return withAWSRetry(() => serviceFunction(s3Service, ...args));
    };

    return wrappedFunction;
  };
}

function buildLambdaServiceModule(
  wrapFn: ReturnType<typeof createLambdaServiceWrapper>,
): WrappedServiceModule<CombinedLambdaServices> {
  return {
    ...Object.entries(lambdaService).reduce(
      (acc, [key, func]) => ({
        ...acc,
        [key]: wrapFn(func as ServiceFunction, key),
      }),
      {},
    ),
    // TODO: Make email service return a lambda function so we can wrap it consistently
    // Explicitly include email service functions
    draftConfirmationEmailForAppointment: wrapFn(
      emailService.draftConfirmationEmailForAppointment,
      "draftConfirmationEmailForAppointment",
    ),
    draftRecordsFollowUpEmailForAppointment: wrapFn(
      emailService.draftRecordsFollowUpEmailForAppointment,
      "draftRecordsFollowUpEmailForAppointment",
    ),
    draftAttendanceEmailForAppointment: wrapFn(
      emailService.draftAttendanceEmailForAppointment,
      "draftAttendanceEmailForAppointment",
    ),
    draftPreVisitCallSchedulingEmailForAppointment: wrapFn(
      emailService.draftPreVisitCallSchedulingEmailForAppointment,
      "draftPreVisitCallSchedulingEmailForAppointment",
    ),
  } as WrappedServiceModule<CombinedLambdaServices>;
}

// Temporary fix: these long-running functions exceed the default 120s SDK
// socket timeout, causing duplicate invocations that MAs reported as
// duplicated generation results. See specs/long-running-lambda-invocation-fix.md
const LONG_RUNNING_FUNCTIONS: [string, ServiceFunction][] = [
  ["generatePreHistoryTemplate", lambdaService.generatePreHistoryTemplate],
  // (Impairment rating is now fire-and-forget via triggerImpairmentRating — it
  // returns immediately with a 202, so it no longer needs the extended client.)
  // Single-segment re-summarization runs the LangGraph pipeline synchronously;
  // a single LLM call can easily exceed the default 120s SDK socket timeout.
  ["resummarizeSegment", lambdaService.resummarizeSegment],
  // Renders every MRR entry to a .docx; large MRRs run for minutes
  // (lambda timeout 600s).
  ["generateRecordBriefingDocx", lambdaService.generateRecordBriefingDocx],
  // Sub-rosa entry summarization downloads the video, uploads it to Gemini
  // and waits for ingest — minutes for a wide window.
  ["summarizeSubRosaEntry", lambdaService.summarizeSubRosaEntry],
  // Triage V2 orchestration runs for minutes (agent loop) — far past the
  // default 120s SDK socket timeout.
  ["runTriageV2", lambdaService.runTriageV2],
  // History-call transcript populate: LLM call over a possibly-long
  // transcript can exceed 120s; lambda timeout is 300s.
  ["populateHistoryTranscript", lambdaService.populateHistoryTranscript],
  // History merge: one LLM call reconciling every populated note field on the
  // tab, so wall time scales with how much is filled; lambda timeout is 330s.
  ["mergePopulatedSources", lambdaService.mergePopulatedSources],
  // Party consolidation reads every cover letter a party sent in one prompt,
  // so its latency scales with letter count; lambda timeout is 600s.
  ["consolidateParty", lambdaService.consolidateParty],
  // On-site ops backfill scans + reassigns every upcoming appointment at a
  // location; a doctor with a far-back effective date can exceed the default
  // 120s SDK socket timeout. Now invoked synchronously to report completion.
  ["backfillOnsiteOps", lambdaService.backfillOnsiteOps],
  // HOI generation runs several LLM calls then a chunked Google Doc write; a
  // retry past 120s would start a second writer on the same doc (GET-4077).
  // Lambda timeout is 300s.
  [
    "generateHistoryOfInjuriesDynamic",
    lambdaService.generateHistoryOfInjuriesDynamic,
  ],
];

// Builds the wrapped service modules from the shared AWS clients. Pure — the
// wrappers are stateless (correlation context is generated per call), so a
// single instance can safely be shared across the whole app via context.
function buildServices(lambda: AWS.Lambda | null, s3: AWS.S3 | null): Services {
  if (!lambda || !s3) {
    return {
      lambda: null,
      silentLambda: null,
      s3: null,
    };
  }

  const wrapLambda = createLambdaServiceWrapper(lambda);
  const wrapSilentLambda = createLambdaServiceWrapper(lambda, true);
  const wrapS3 = createS3ServiceWrapper(s3);

  // Long-running Lambda client with extended timeout to prevent SDK socket
  // timeouts from triggering duplicate invocations. 960s covers the max
  // Lambda timeout (900s) plus cold start overhead.
  const longRunningLambdaClient = new AWS.Lambda({
    httpOptions: { timeout: 960_000 },
  });
  const wrapLongRunningLambda = createLambdaServiceWrapper(
    longRunningLambdaClient,
  );
  const wrapSilentLongRunningLambda = createLambdaServiceWrapper(
    longRunningLambdaClient,
    true,
  );

  const buildOverrides = (
    wrapFn: ReturnType<typeof createLambdaServiceWrapper>,
  ) =>
    Object.fromEntries(
      LONG_RUNNING_FUNCTIONS.map(([key, fn]) => [key, wrapFn(fn, key)]),
    );

  const lambdaServices = {
    ...buildLambdaServiceModule(wrapLambda),
    ...buildOverrides(wrapLongRunningLambda),
  } as WrappedServiceModule<CombinedLambdaServices>;
  const silentLambdaServices = {
    ...buildLambdaServiceModule(wrapSilentLambda),
    ...buildOverrides(wrapSilentLongRunningLambda),
  } as WrappedServiceModule<CombinedLambdaServices>;

  const s3Services = Object.entries(s3Service).reduce(
    (acc, [key, func]) => ({
      ...acc,
      [key]: wrapS3(func as ServiceFunction),
    }),
    {} as WrappedServiceModule<typeof s3Service>,
  );

  return {
    lambda: lambdaServices,
    silentLambda: silentLambdaServices,
    s3: s3Services,
  };
}

const ServicesContext = createContext<Services | null>(null);

/**
 * Builds the wrapped Lambda/S3 service modules once from the shared AWS clients
 * and provides them via context. Must be mounted inside `AuthSessionProvider`
 * so it sees the `null -> client` transition; the value rebuilds exactly once
 * when auth resolves.
 */
export const ServicesProvider = ({ children }: { children: ReactNode }) => {
  const { lambda, s3 } = useAuthSession();
  const services = useMemo(() => buildServices(lambda, s3), [lambda, s3]);

  return (
    <ServicesContext.Provider value={services}>
      {children}
    </ServicesContext.Provider>
  );
};

export const useServices = (): Services => {
  const services = useContext(ServicesContext);
  if (!services) {
    throw new Error("useServices must be used within a ServicesProvider");
  }
  return services;
};
